export type AdminCropPresetId = 'catalog_wide' | 'vitrine_tile' | 'square' | 'full_size';

export type AdminCropPreset = {
  id: AdminCropPresetId;
  label: string;
  hint: string;
  /** Фиксированное соотношение; при naturalAspect — игнорируется. */
  aspect: number;
  outW: number;
  /** Кадр как у исходника (с pinch/pan), экспорт до outW по длинной стороне. */
  naturalAspect?: boolean;
};

export const ADMIN_CROP_PRESETS: Record<AdminCropPresetId, AdminCropPreset> = {
  catalog_wide: {
    id: 'catalog_wide',
    label: 'Широкая',
    hint: '16:9 · карусель и каталог',
    aspect: 16 / 9,
    outW: 1280,
  },
  vitrine_tile: {
    id: 'vitrine_tile',
    label: 'Плитка',
    hint: '4:3 · подтип на витрине',
    aspect: 4 / 3,
    outW: 960,
  },
  square: {
    id: 'square',
    label: 'Квадрат',
    hint: '1:1 · миниатюра',
    aspect: 1,
    outW: 1080,
  },
  full_size: {
    id: 'full_size',
    label: 'Полный кадр',
    hint: 'Как оригинал · до 2560 px',
    aspect: 0,
    outW: 2560,
    naturalAspect: true,
  },
};

export const ADMIN_CROP_PRESET_ORDER: AdminCropPresetId[] = [
  'catalog_wide',
  'square',
  'full_size',
  'vitrine_tile',
];

/** Карусель PDP: широкая, квадрат, полный кадр (+ плитка при необходимости). */
export const CATALOG_PDP_CROP_PRESETS: AdminCropPresetId[] = [
  'catalog_wide',
  'square',
  'full_size',
  'vitrine_tile',
];

export function resolveCropAspect(
  preset: AdminCropPreset,
  naturalW: number,
  naturalH: number,
): number {
  if (preset.naturalAspect && naturalW > 0 && naturalH > 0) {
    return naturalW / naturalH;
  }
  return preset.aspect > 0 ? preset.aspect : naturalW / naturalH || 16 / 9;
}

export type AdminCropTransform = {
  zoom: number;
  panX: number;
  panY: number;
};

export const DEFAULT_CROP_TRANSFORM: AdminCropTransform = {
  zoom: 1,
  panX: 0,
  panY: 0,
};

export async function readFileAsDataUrl(file: File): Promise<string> {
  return new Promise((resolve, reject) => {
    const reader = new FileReader();
    reader.onload = () => resolve(String(reader.result || ''));
    reader.onerror = () => reject(new Error('read failed'));
    reader.readAsDataURL(file);
  });
}

export async function buildCroppedImageFile(
  source: string,
  fileName: string,
  preset: AdminCropPreset,
  transform: AdminCropTransform,
): Promise<File | null> {
  const img = new Image();
  img.src = source;
  await new Promise<void>((resolve, reject) => {
    img.onload = () => resolve();
    img.onerror = () => reject(new Error('image load failed'));
  });

  const naturalW = img.naturalWidth || 0;
  const naturalH = img.naturalHeight || 0;
  if (!naturalW || !naturalH) return null;

  const aspect = resolveCropAspect(preset, naturalW, naturalH);
  const zoom = Math.max(1, Math.min(3, transform.zoom));
  let cropW = naturalW / zoom;
  let cropH = cropW / aspect;
  if (cropH > naturalH / zoom) {
    cropH = naturalH / zoom;
    cropW = cropH * aspect;
  }

  const centerX = (naturalW - cropW) / 2;
  const centerY = (naturalH - cropH) / 2;
  const maxShiftX = Math.max(0, (naturalW - cropW) / 2);
  const maxShiftY = Math.max(0, (naturalH - cropH) / 2);
  const sx = Math.max(0, Math.min(naturalW - cropW, centerX + (transform.panX / 100) * maxShiftX));
  const sy = Math.max(0, Math.min(naturalH - cropH, centerY + (transform.panY / 100) * maxShiftY));

  let outW: number;
  let outH: number;
  if (preset.naturalAspect) {
    const maxEdge = preset.outW;
    outW = Math.max(1, Math.round(cropW));
    outH = Math.max(1, Math.round(cropH));
    if (Math.max(outW, outH) > maxEdge) {
      if (outW >= outH) {
        outW = maxEdge;
        outH = Math.max(1, Math.round(maxEdge / aspect));
      } else {
        outH = maxEdge;
        outW = Math.max(1, Math.round(maxEdge * aspect));
      }
    }
  } else {
    outW = preset.outW;
    outH = Math.round(outW / aspect);
  }
  const canvas = document.createElement('canvas');
  canvas.width = outW;
  canvas.height = outH;
  const ctx = canvas.getContext('2d');
  if (!ctx) return null;
  ctx.drawImage(img, sx, sy, cropW, cropH, 0, 0, outW, outH);

  const blob = await new Promise<Blob | null>((resolve) => {
    canvas.toBlob((b) => resolve(b), 'image/jpeg', 0.92);
  });
  if (!blob) return null;
  const safeName = fileName.replace(/\.[a-zA-Z0-9]+$/, '') || 'image';
  return new File([blob], `${safeName}.jpg`, { type: 'image/jpeg' });
}
